import { useEffect, useRef, useState } from 'react';
import { IconCheck, IconCopy, IconMinus } from '@tabler/icons';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Timeline,
} from '#root/components/ui';
import { SparksIcon } from '#root/icons';
import { travelerService } from '#root/services';
import * as ROUTES from '#root/constants/navigation';
import { parseYmd } from '#root/utils';
import type {
  MatchEligibilityDTO,
  MatchGroupDTO,
  MatchReservationDTO,
  MatchReserveDTO,
} from '#root/models/PrepaidMatchDTO';
import { dayMonth, deliverByInfo, formatReservationTime, usd } from './matchFormat';
import { buildReservationMessage, useCopyMessage } from './matchMessage';
import styles from './match.module.scss';

const DATE_HINT = 'Mientras antes la entregues (con certeza), más chance de que te la asignen';

interface MatchWantDialogProps {
  open: boolean;
  /**
   * Se conserva al cerrar para que la animación de salida no pierda el contenido.
   * `null` cuando se abre desde la franja de requisitos (solo modo no elegible).
   */
  group: MatchGroupDTO | null;
  eligibility: MatchEligibilityDTO;
  /** `server_time` del feed: "hoy" en Lima, que es contra lo que valida el BE */
  serverTime: string;
  /** `changed`: hubo un intento de reservar (éxito o error del BE), así que el feed se actualiza */
  onClose: (changed: boolean) => void;
  onGoToAll: () => void;
}

/** "$100", "$100 y $80", "$100, $80 y $60" */
const joinList = (items: string[]) =>
  items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;

const MatchWantDialog = ({
  open,
  group,
  eligibility,
  serverTime,
  onClose,
  onGoToAll,
}: MatchWantDialogProps) => {
  const [deliveryDate, setDeliveryDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reservation, setReservation] = useState<MatchReservationDTO | null>(null);
  const attempted = useRef(false);
  const { copied, copy } = useCopyMessage(reservation ? buildReservationMessage(reservation) : '');

  // Cada apertura empieza en el formulario con la fecha del viaje. Se resetea al abrir y no
  // al cerrar: durante la animación de salida el contenido sigue visible.
  useEffect(() => {
    if (!open) return;
    setDeliveryDate(eligibility.trip?.fecha_entrega ?? '');
    setSubmitting(false);
    setError(null);
    setReservation(null);
    attempted.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group?.group_key]);

  const handleClose = () => onClose(attempted.current);

  const plural = (group?.orders.length ?? 0) > 1;

  if (!eligibility.eligible) {
    const { reason, trip, offers_count, purchased_count, required_offers, required_purchased } =
      eligibility;
    const hasOffers = offers_count >= required_offers;
    const hasPurchased = purchased_count >= required_purchased;

    const checks = [
      { done: reason !== 'not_capable', label: 'Capacitación completa' },
      { done: trip !== null, label: 'Viaje activo' },
      {
        done: hasOffers || hasPurchased,
        label: `${required_offers} ofertas en tu viaje actual`,
        hint: `Llevas ${offers_count}/${required_offers}`,
        or: {
          label: `${required_purchased} acuerdo comprado en tu viaje actual`,
          hint: `Llevas ${purchased_count}/${required_purchased}`,
        },
      },
    ];

    return (
      <Modal open={open} onClose={handleClose}>
        <ModalHeader icon={<SparksIcon />} tone="match">
          <ModalTitle>Desbloquea los Pedidos Match</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <ul className={styles.checklist}>
            {checks.map((check) => (
              <li
                key={check.label}
                className={`${styles.check} ${check.done ? styles.checkDone : ''}`}>
                <span className={styles.checkIcon} aria-hidden="true">
                  {check.done ? <IconCheck size={14} /> : <IconMinus size={14} />}
                </span>
                <span>
                  {check.label}
                  {check.hint && <small className={styles.checkHint}>{check.hint}</small>}
                  {check.or && (
                    <>
                      <small className={styles.checkHint}>o</small>
                      {check.or.label}
                      <small className={styles.checkHint}>{check.or.hint}</small>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {group && (
            <p className={styles.unlock}>
              {plural
                ? 'Con estos pedidos recibirías comisiones netas de'
                : 'Con este pedido recibirías una comisión neta de'}{' '}
              <strong>{joinList(group.orders.map((order) => usd(order.earning)))}</strong>.
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          {reason === 'no_trip' && (
            <Button variant="outline" to={ROUTES.TRAVELER_TRIPS}>
              Mis viajes
            </Button>
          )}
          {reason === 'not_capable' && (
            <Button variant="outline" to={ROUTES.TRAVELER_TRAINING}>
              Capacitarme
            </Button>
          )}
          <Button onClick={onGoToAll}>Hacer ofertas</Button>
        </ModalFooter>
      </Modal>
    );
  }

  // Un elegible siempre llega con un pedido elegido
  if (!group) return null;

  const names = group.orders.map((order) => order.name).join(' · ');
  const minDate = serverTime.slice(0, 10);
  const maxDate = eligibility.trip?.fecha_entrega ?? '';
  // `Y-m-d` se ordena como texto
  const dateValid =
    deliveryDate !== '' && deliveryDate >= minDate && (maxDate === '' || deliveryDate <= maxDate);
  const maxLabel = parseYmd(maxDate);

  const handleReserve = async () => {
    if (!dateValid || submitting) return;
    setSubmitting(true);
    setError(null);
    attempted.current = true;
    const res = await travelerService.matchReserve({
      order_ids: group.orders.map((order) => order.idp),
      delivery_date: deliveryDate,
    });
    setSubmitting(false);
    const created = (res.data as MatchReserveDTO | null)?.reservation;
    if (res.error || !created) {
      // El mensaje exacto del BE ("ya fue reservado por otro viajero", cupo, bloqueo…)
      setError(res.message ?? 'No se pudo reservar.');
      return;
    }
    setReservation(created);
  };

  if (!reservation) {
    return (
      <Modal open={open} onClose={handleClose}>
        <ModalHeader icon={<SparksIcon />} tone="match">
          <ModalTitle>Reserva tu turno</ModalTitle>
          <ModalDescription>{names}</ModalDescription>
        </ModalHeader>
        <ModalBody>
          <p className={styles.lead}>
            Al reservar, nadie más puede {plural ? 'reservarlos' : 'reservarlo'}. Luego envías un
            mensaje a tu grupo de WhatsApp y esperas nuestra confirmación para{' '}
            {plural ? 'comprarlos' : 'comprarlo'}.
          </p>
          <Input
            id="match-delivery-date"
            type="date"
            fullWidth
            label={plural ? '¿Cuándo puedes entregarlos?' : '¿Cuándo puedes entregarlo?'}
            value={deliveryDate}
            min={minDate}
            max={maxDate || undefined}
            error={!dateValid}
            helperText={
              !dateValid && maxLabel
                ? `La fecha debe estar entre hoy y el ${dayMonth(maxLabel)}`
                : DATE_HINT
            }
            onChange={(event) => setDeliveryDate(event.target.value)}
          />
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button loading={submitting} disabled={!dateValid} onClick={handleReserve}>
            Reservar
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  const deadline = deliverByInfo(group.deliver_by, serverTime);
  const packagings = Array.from(new Set(group.orders.map((order) => order.packaging).filter(Boolean)));

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalHeader icon={<SparksIcon />} tone="match">
        <ModalTitle>¡Reservado!</ModalTitle>
        <ModalDescription>Ahora envía este mensaje a tu grupo de WhatsApp</ModalDescription>
      </ModalHeader>
      <ModalBody>
        <pre className={styles.message}>{buildReservationMessage(reservation)}</pre>
        <p className={styles.deadline}>
          Tienes hasta {formatReservationTime(reservation.reserved_expires_at, serverTime, true)}{' '}
          para coordinar con nosotros.
        </p>
        <Timeline
          variant="numbered"
          items={[
            'Espera nuestra confirmación en el grupo',
            `Con la confirmación, ${plural ? 'cómpralos' : 'cómpralo'}`,
            'Envía el pantallazo de la compra por el grupo',
          ]}
        />
        <div className={styles.beforeBuy}>
          <strong>Antes de comprar</strong>
          <ul>
            <li>
              {plural ? 'Cómpralos' : 'Cómpralo'} exactamente como se indica: modelo, color,
              capacidad
              {packagings.length > 0 && ` y empaque (${packagings.join(' / ')})`}.
            </li>
            {deadline && <li>{deadline.label}.</li>}
          </ul>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button icon={copied ? <IconCheck size={18} /> : <IconCopy size={18} />} onClick={copy}>
          {copied ? 'Copiado' : 'Copiar mensaje'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default MatchWantDialog;
